/**
 * Sensitivity Tornado Chart Component
 * Displays ±10% variation impacts on total demand
 */

import React from 'react';
import { FinancialValue } from './shared/FinancialValue';

interface SensitivityTornadoChartProps {
  result: any;
}

export default function SensitivityTornadoChart({ result }: SensitivityTornadoChartProps) {
  if (!result || !result.drivers) return null;

  const { base_demand, variation_pct, drivers, top_driver } = result;

  // Find max swing for scaling
  const maxSwing = Math.max(...drivers.map((d: any) => d.total_swing));

  return (
    <div
      className="border-2 rounded-lg p-6 space-y-6"
      style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
    >
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Sensitivity Analysis</h4>
          <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
            Impact of ±{variation_pct}% variation on key drivers
          </p>
        </div>
        <svg className="w-12 h-12" style={{ color: 'var(--color-needs-human-text)' }} fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
      </div>

      {/* Base Demand */}
      <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Base Total Demand</p>
        <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
          <FinancialValue value={base_demand} />
        </p>
      </div>

      {/* Top Driver Highlight */}
      {top_driver && (
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start space-x-2">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
              <p className="font-semibold">Top Driver: {top_driver}</p>
              <p>This parameter has the largest impact on total demand</p>
            </div>
          </div>
        </div>
      )}

      {/* Tornado Chart */}
      <div className="space-y-3">
        <h5 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Tornado Chart (Sorted by Impact)</h5>

        {drivers.map((driver: any, idx: number) => {
          const lowPct = (Math.abs(driver.low_impact) / maxSwing) * 100;
          const highPct = (Math.abs(driver.high_impact) / maxSwing) * 100;
          const isNegativeLow = driver.low_impact < 0;
          const isNegativeHigh = driver.high_impact < 0;

          return (
            <div key={idx} className="rounded-lg p-4 border transition-colors" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              {/* Driver Name */}
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{driver.driver}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Total Swing: <FinancialValue value={driver.total_swing} />
                </p>
              </div>

              {/* Tornado Bars */}
              <div className="relative h-12 flex items-center">
                {/* Center line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full z-10" style={{ backgroundColor: 'var(--chart-axis)' }}></div>

                {/* Low bar (left side) */}
                <div
                  className="absolute right-1/2 h-8 transition-all hover:opacity-80 flex items-center justify-end pr-2"
                  style={{ width: `${lowPct}%`, backgroundColor: isNegativeLow ? 'var(--chart-negative)' : 'var(--chart-positive)' }}
                >
                  <span className="text-xs font-medium" style={{ color: 'var(--color-on-brand)' }}>
                    {driver.low_pct > 0 ? '+' : ''}{driver.low_pct}%
                  </span>
                </div>

                {/* High bar (right side) */}
                <div
                  className="absolute left-1/2 h-8 transition-all hover:opacity-80 flex items-center justify-start pl-2"
                  style={{ width: `${highPct}%`, backgroundColor: isNegativeHigh ? 'var(--chart-negative)' : 'var(--chart-positive)' }}
                >
                  <span className="text-xs font-medium" style={{ color: 'var(--color-on-brand)' }}>
                    {driver.high_pct > 0 ? '+' : ''}{driver.high_pct}%
                  </span>
                </div>
              </div>

              {/* Values */}
              <div className="flex justify-between mt-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                <span>Low: <FinancialValue value={driver.low_demand} /></span>
                <span style={{ color: 'var(--color-text-dim)' }}>Base: <FinancialValue value={driver.base_demand} /></span>
                <span>High: <FinancialValue value={driver.high_demand} /></span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Interpretation */}
      <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
            <p className="font-medium mb-1">How to Read This Chart:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Each bar shows the impact of varying that driver by ±{variation_pct}%</li>
              <li><span className="font-semibold">Left (red/green)</span>: Impact of DECREASING the driver</li>
              <li><span className="font-semibold">Right (green/red)</span>: Impact of INCREASING the driver</li>
              <li>Longer bars = more sensitive to that assumption</li>
              <li>Focus negotiations on top 2-3 drivers for maximum leverage</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Methodology Note */}
      <div className="text-xs rounded p-3 border" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <p className="font-medium mb-1">Methodology:</p>
        <p>{result.methodology}</p>
      </div>
    </div>
  );
}
